import { Paper, Text, Group, createStyles, Box, ActionIcon, ScrollArea, Table, Badge } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useDasboardApi from '../../hooks/useDashboardApi';
import BenefitInformationFilterSelector from './selectors/BenefitInformationFilterSelector';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: '20px 0 10px'
	},
	text: {
		fontSize: 36,
		lineHeight: '42px',
		fontWeight: 300,
	},
	cardTitle: {
		fontWeight: 600,
		fontSize: 16,
		lineHeight: '20px',
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
	},
	th: {
		padding: '0 !important',
	},
}));

export default function BenefitInformation() {

	// State and hooks
	const { classes } = useStyles();
	const { getBenefitInformation } = useDasboardApi();
	const benefitInformation = useSelector(state => state.dashboardData.benefitInformation);
	const [filteredData, setFilteredData] = useState([]);
	const [filterOptions, setFilterOptions] = useState([]);
	const [categoryFilters, setCategoryFilters] = useState([]);
	const [levelFilters, setLevelFilters] = useState([]);
	const [dateOrder, setDateOrder] = useState('');
	const [stockOrder, setStockOrder] = useState('');
	const navigate = useNavigate();

	// On load
	useEffect(() => {
		async function fetchData() {
			await getBenefitInformation();
		}
		fetchData();
		// eslint-disable-next-line
	}, []);

	// On data load
	useEffect(() => {

		// If not null
		if (benefitInformation) {

			// Get unique filters
			const uniqueFilters = benefitInformation.reduce((acc, curr) => {
				acc.categories = acc.categories.includes(curr.category) ? acc.categories : [...acc.categories, curr.category];
				acc.levels = acc.levels.includes(curr.level) ? acc.levels : [...acc.levels, curr.level];
				return acc;
			}, { categories: [], levels: [] });
			setFilterOptions(uniqueFilters);

			// Set data 1:1
			setFilteredData(benefitInformation);

		}

	}, [benefitInformation]);

	// Order by date
	const orderByDate = (dateType = 'startDate') => {

		// Order and update
		const newData = filteredData.slice().sort((a, b) => new Date(a[dateType]) - new Date(b[dateType]));
		setDateOrder(dateType);
		setFilteredData(newData);

	}

	// Order by stock
	const orderByStock = (order = 'more') => {

		// Order and update
		const newData = filteredData.slice().sort((a, b) => {
			const stockDiffA = a.stock - a.available;
			const stockDiffB = b.stock - b.available;
			return order === 'more' ? stockDiffB - stockDiffA : stockDiffA - stockDiffB;
		});
		setStockOrder(order);
		setFilteredData(newData);

	}

	// TH component
	const Th = ({ children }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				Nivel
			</Th>
			<Th>
				Categoría
			</Th>
			<Th>
				Beneficio
			</Th>
			<Th>
				Desde
			</Th>
			<Th>
				Hasta
			</Th>
			<Th>
				Stock
			</Th>
			<Th>
				Partner
			</Th>
		</tr>
	);

	// Build rows
	const rows = filteredData?.map((element) => (
		<tr key={Math.random()}>
			<td>
				{element.level}
			</td>
			<td>
				<Badge
					variant="light"
					color="grape"
				>
					{element.category}
				</Badge>
			</td>
			<td>
				{element.name}
			</td>
			<td>
				{element.startDate ? dayjs(new Date(element.startDate)).format('DD/MM/YYYY') : 'N/A'}
			</td>
			<td>
				{element.endDate ? dayjs(new Date(element.endDate)).format('DD/MM/YYYY') : 'N/A'}
			</td>
			<td>
				{element.available} / {element.stock}
			</td>
			<td>
				{element.partner.name}
			</td>
		</tr>
	));

	// Reset filters
	const resetFilters = () => {

		// Reset state filters
		setStockOrder('');
		setDateOrder('');
		setFilteredData(benefitInformation);
		setLevelFilters([]);
		setCategoryFilters([]);

	}

	// Level filters
	useEffect(() => {

		// If there are filters
		if (levelFilters.length > 0) {

			// Filter
			setFilteredData(benefitInformation.filter(el => levelFilters.includes(el.level)));

		} else {

			// Reset
			setFilteredData(benefitInformation);

		}

		// eslint-disable-next-line
	}, [levelFilters]);

	// Category filters
	useEffect(() => {

		// If there are filters
		if (categoryFilters.length > 0) {

			// Filter
			setFilteredData(benefitInformation.filter(el => categoryFilters.includes(el.category)));

		} else {

			// Reset
			setFilteredData(benefitInformation);

		}

		// eslint-disable-next-line
	}, [categoryFilters]);

	return (
		<Paper
			withBorder
			p={12}
			style={{ position: 'relative' }}
			radius={18}
		>
			{/* Top */}
			<Group
				position="apart"
				noWrap
			>
				{/* Title */}
				<Text className={classes.cardTitle}>
					Información
				</Text>
				{/* New */}
				<ActionIcon
					variant="filled"
					onClick={() => navigate('/benefits?add=1')}
					color="mainPurple"
				>
					<IconPlus
						size={18}
						stroke={2}
					/>
				</ActionIcon>
			</Group>
			{/* Time & Filter */}
			<Group
				position="apart"
				mt={8}
				noWrap
				align="center"
			>
				{/* Empty */}
				&nbsp;
				{/* Filter */}
				<BenefitInformationFilterSelector
					selectedCategories={[categoryFilters, setCategoryFilters]}
					selectedLevels={[levelFilters, setLevelFilters]}
					options={filterOptions}
					orderByDate={orderByDate}
					dateOrder={dateOrder}
					orderByStock={orderByStock}
					stockOrder={stockOrder}
					resetFilters={resetFilters}
				/>
			</Group>
			{/* Content */}
			<Box className={classes.container}>
				{/* Table */}
				<ScrollArea>
					<Table
						highlightOnHover
						sx={{ flex: 2 }}
					>
						<thead>{ths}</thead>
						<tbody>{rows}</tbody>
					</Table>
				</ScrollArea>
			</Box>
		</Paper>
	);

}
